import {useMutation, useSuspenseQuery} from '@tanstack/react-query';

import {toastRequestError} from '_utils/axios';

import type {DeskSearch, DeskStore, DeskUpdate} from "_types/requests/desk";
import type {Desk} from "_types/models/Desk";

import {apiSwr, routes} from '_api';
import {deleteDesk, findDesk, searchDesks, storeDesk, updateDesk} from "_api/fetchers/desks";

export function useDeskSearch(search: DeskSearch) {
    const queryKey = [...routes.desks.search, search] as const;

    const swr = useSuspenseQuery({
        queryKey,
        queryFn: searchDesks,
    });

    const paginator = swr.data;
    const desks = paginator?.data ?? [];
    const meta = paginator?.meta;

    return {...swr, paginator, desks, meta} as const;
}

export function useDesk(id: Desk['id']) {
    const queryKey = [...routes.desk(id).show] as const;

    const swr = useSuspenseQuery({
        queryKey,
        queryFn: findDesk,
    });
    const desk = swr.data;

    return {...swr, desk} as const;
}

export function useDeskStore() {
    const mutationKey = [...routes.desks.store] as const;

    return useMutation({
        mutationKey,
        mutationFn: async (payload: DeskStore) => {
            return storeDesk(mutationKey, payload);
        },

        onMutate: async d => {
            await apiSwr.cancelQueries({queryKey: [...routes.desks.search]});
        },
        onSuccess: async d => {
            await apiSwr.invalidateQueries({queryKey: [...routes.desks.search]});
            await apiSwr.invalidateQueries({queryKey: [...routes.desk(d.id).show]});
        },
        onError: err => {
            toastRequestError(err);
        },
    });
}

export function useDeskUpdate(id: Desk['id']) {
    const mutationKey = [...routes.desk(id).update] as const;

    return useMutation({
        mutationKey,
        mutationFn: async (payload: DeskUpdate) => {
            return updateDesk(mutationKey, payload);
        },

        onMutate: async d => {
            await apiSwr.cancelQueries({queryKey: [...routes.desks.search]});
            await apiSwr.cancelQueries({queryKey: [...routes.desk(id).show]});
        },
        onSuccess: async d => {
            await apiSwr.invalidateQueries({queryKey: [...routes.desks.search]});
            await apiSwr.invalidateQueries({queryKey: [...routes.desk(d.id).show]});
        },
        onError: err => {
            toastRequestError(err);
        },
    });
}

export function useDeskDelete(id: Desk['id']) {
    const mutationKey = [...routes.desk(id).delete] as const;

    return useMutation({
        mutationKey,
        mutationFn: async () => {
            return deleteDesk(mutationKey);
        },

        onMutate: async d => {
            await apiSwr.cancelQueries({queryKey: [...routes.desks.search]});
            await apiSwr.cancelQueries({queryKey: [...routes.desk(id).show]});
        },
        onSuccess: async d => {
            await apiSwr.invalidateQueries({queryKey: [...routes.desks.search]});
            await apiSwr.invalidateQueries({queryKey: [...routes.desk(id).show]});
        },
        onError: err => {
            toastRequestError(err);
        },
    });
}
